import { useTranslation } from 'react-i18next';
import { array, boolean, mixed, number, object, string } from 'yup';
import { EDIT_FUNCTION_MODAL_TRANSLATE_PATH } from 'components/admin/helios/Functions/Modals/EditFunctionModal.constants';

const ADD_FUNCTION_MODAL_TRANSLATE_PATH = 'helios.functions.addFunctionModal';
const ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH = 'helios.functions.addFunctionParametersModal';
const IMPORT_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH = 'helios.functions.importFunctionModal';

export const useImportFunctionTemplateModalSchema = () => {
  const { t } = useTranslation();

  const dataSourceLabel = t(`${IMPORT_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.dataSource`).toLowerCase();
  const idLabel = t(`${IMPORT_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.id`).toLowerCase();
  const nameLabel = t(`${IMPORT_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.name`).toLowerCase();
  const isCommonLabel = t(`${IMPORT_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.isCommon`).toLowerCase();
  const moduleLabel = t(`${IMPORT_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.module`).toLowerCase();

  return object({
    dataSourceId: string().required(t('common.requiredWithFieldName', { fieldName: dataSourceLabel })),
    dataSourceName: string(),
    heliosFunctionId: number().required(t('common.requiredWithFieldName', { fieldName: idLabel })),
    functionName: string().required(t('common.requiredWithFieldName', { fieldName: nameLabel })),
    isCommon: boolean().required(t('common.requiredWithFieldName', { fieldName: isCommonLabel })),
    importCodeListTemplates: boolean(),
    moduleId: string().when('importCodeListTemplates', {
      is: true,
      then: (schema) => schema.required(t('common.requiredWithFieldName', { fieldName: moduleLabel })),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
};

export const useAddFunctionParametersModalSchema = () => {
  const { t } = useTranslation();

  const nameLabel = t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.name`).toLowerCase();
  const keyLabel = t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.key`).toLowerCase();
  const overviewSelectLabel = t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.overviewSelect`).toLowerCase();

  return object({
    id: string().nullable(),
    name: string().required(t('common.requiredWithFieldName', { fieldName: nameLabel })),
    labelText: string().label(t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.label`)),
    functionParameterType: string().required(),
    dateFormatVariant: string().nullable().oneOf(['Date', 'DateTime', 'Time', 'MonthYear']),
    order: number()
      .min(0)
      .label(t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.fields.order`)),
    isRequired: boolean(),
    isMultiSelect: boolean().default(false),
    functionParameterSelectedList: array()
      .of(
        object({
          name: string().required(t('common.requiredWithFieldName', { fieldName: keyLabel })),
          value: string(),
        })
      )
      .default([])
      .nullable(),
    templateBrowseId: string()
      .nullable()
      .when('functionParameterType', {
        is: 'Relation',
        then: (schema) => schema.required(t('common.requiredWithFieldName', { fieldName: overviewSelectLabel })),
        otherwise: (schema) => schema.notRequired(),
      }),
  });
};

export const useAddFunctionModalSchema = () => {
  const { t } = useTranslation();

  const idLabel = t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.id`).toLowerCase();
  const nameLabel = t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.name`).toLowerCase();
  const functionTypeLabel = t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.functionType`).toLowerCase();
  const datasourceIdLabel = t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.dataSource`).toLowerCase();

  const funcParamsSchema = useAddFunctionParametersModalSchema();

  return object({
    name: string().required(t('common.requiredWithFieldName', { fieldName: nameLabel })),
    column: string(),
    functionType: string().required(t('common.requiredWithFieldName', { fieldName: functionTypeLabel })),
    heliosFunctionId: number().required(t('common.requiredWithFieldName', { fieldName: idLabel })),
    isCommon: boolean(),
    entryFunction: boolean(),
    skipRecordsOnError: boolean(),
    parentColumn: string().when('entryFunction', {
      is: true,
      then: (schema) =>
        schema.required(
          t('common.requiredWithFieldName', { fieldName: t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.parentColumn`).toLowerCase() })
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
    datasourceId: string().required(t('common.requiredWithFieldName', { fieldName: datasourceIdLabel })),
    datasourceName: string(),
    functionWorkflowCommentType: string().when('functionType', {
      is: 'HegWorkflow',
      then: (schema) =>
        schema.required(
          t('common.requiredWithFieldName', { fieldName: t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.functionWorkflowCommentType`).toLowerCase() })
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
    functionParameters: array().of(funcParamsSchema),
    currentParameter: mixed().nullable(),
    translatesName: array().of(
      object({
        language: string(),
        value: string(),
      })
    ),
  });
};

export const useEditFunctionModalSchema = () => {
  const { t } = useTranslation();

  const functionParametersSchema = useAddFunctionParametersModalSchema();

  const dataSourceLabel = t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.dataSource`).toLowerCase();
  const nameLabel = t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.name`).toLowerCase();
  const typeLabel = t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.type`).toLowerCase();

  return object({
    id: string(),
    column: string(),
    datasourceId: string().required(t('common.requiredWithFieldName', { fieldName: dataSourceLabel })),
    datasourceName: string().nullable(),
    entryFunction: boolean(),
    isCommon: boolean(),
    skipRecordsOnError: boolean(),
    heliosFunctionId: number(),
    number: number(),
    name: string().required(t('common.requiredWithFieldName', { fieldName: nameLabel })),
    parentColumn: string().when('entryFunction', {
      is: true,
      then: (schema) =>
        schema.required(
          t('common.requiredWithFieldName', { fieldName: t(`${ADD_FUNCTION_MODAL_TRANSLATE_PATH}.fields.parentColumn`).toLowerCase() })
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
    functionType: string().required(t('common.requiredWithFieldName', { fieldName: typeLabel })),
    functionWorkflowCommentType: string(),
    functionParameters: array().of(functionParametersSchema),
    currentParameter: mixed().nullable(),
    translatesName: array().of(
      object({
        language: string(),
        value: string(),
      })
    ),
  });
};
